// src/pages/shipment-details/ShipmentDetails.jsx
// @ts-nocheck
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { parseISO, format } from "date-fns";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { assessReturnEligibility } from "../components/createorder/returnEligibility";
import { pushRecentShipment } from "../utils/recentShipments";
import { getOrderStatusName, OrderBusinessStatusEnum } from "../utils/enums";
import { formatInIST } from "../utils/dateHelpers";
import ClaimUploadModal from "../components/ClaimUploadModal";
import CreateTicketModal from "../components/support/CreateTicketModal";
import ticketService from "../services/ticketService";
import useAuth from "../hooks/useAuth";
import OrderWeightDisputeCard from "./weight-dispute/OrderWeightDisputeCard";
import ShipmentChargesCard from "../components/shipment/ShipmentChargesCard";
import { normalizeWeightDisputes } from "./weight-dispute/weightDisputeApi";
import { useToast } from "../components/toast/ToastProvider";
import { motion, useReducedMotion } from "framer-motion";
import {
  RotateCcw,
  Package as PackageIcon,
  Tag,
  Camera,
  FileCheck,
  Layers,
  XCircle,
  ShieldAlert,
  Video,
  MessageSquare,
  RefreshCw,
  Phone,
  Mail,
  CalendarDays,
  ExternalLink,
  Scale as ScaleIcon,
  Map as MapIcon,
  MapPin,
  Activity,
  ClipboardList,
  Download,
  Clock,
  AlertTriangle,
  Copy as CopyIcon,
  Check as CheckIcon,
} from "lucide-react";
import {
  downloadRemoteFile,
  safeFilenameSegment,
} from "../utils/download";

/* ----------------------------- Helpers ----------------------------- */
// Status codes that need support ticket button
const STATUSES_REQUIRING_SUPPORT = [
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.FAILED_DELIVERY.code,
  OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_FAILED.code,
  OrderBusinessStatusEnum.DAMAGED.code,
  OrderBusinessStatusEnum.SHIPMENT_DELAYED.code,
  OrderBusinessStatusEnum.LOST.code,
  OrderBusinessStatusEnum.CONTACT_CUSTOMER_CARE.code,
  OrderBusinessStatusEnum.SHIPMENT_HELD.code,
  OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];

const PICKUP_PENDING_CODES = [
  OrderBusinessStatusEnum.PICKUP_PENDING.code,
  OrderBusinessStatusEnum.OUT_FOR_PICKUP.code,
  OrderBusinessStatusEnum.PICKUP_FAILED.code,
  OrderBusinessStatusEnum.ORDER_PLACED.code,
  OrderBusinessStatusEnum.AWB_REGISTERED.code,
];

/* The forward journey as milestones; a stage is done when ANY history code belongs to it. */
const JOURNEY_STAGES = [
  { label: "Placed", codes: [OrderBusinessStatusEnum.ORDER_PLACED.code, OrderBusinessStatusEnum.AWB_REGISTERED.code, OrderBusinessStatusEnum.MANIFESTED.code] },
  { label: "Pickup", codes: [OrderBusinessStatusEnum.PICKUP_PENDING.code, OrderBusinessStatusEnum.OUT_FOR_PICKUP.code] },
  { label: "Picked up", codes: [OrderBusinessStatusEnum.PICKED_UP.code] },
  { label: "In transit", codes: [OrderBusinessStatusEnum.INTRANSIT.code, OrderBusinessStatusEnum.ORIGIN_CITY_IN.code, OrderBusinessStatusEnum.ORIGIN_CITY_OUT.code, OrderBusinessStatusEnum.DESTINATION_HUB_IN.code] },
  { label: "Out for delivery", codes: [OrderBusinessStatusEnum.OUT_FOR_DELIVERY.code] },
  { label: "Delivered", codes: [OrderBusinessStatusEnum.DELIVERED.code] },
];

/* Once RTO begins the forward journey is over — the rail retells the story as a return. */
const RTO_STAGES = [
  { label: "Placed", codes: [OrderBusinessStatusEnum.ORDER_PLACED.code, OrderBusinessStatusEnum.AWB_REGISTERED.code, OrderBusinessStatusEnum.MANIFESTED.code] },
  { label: "Picked up", codes: [OrderBusinessStatusEnum.PICKED_UP.code] },
  { label: "RTO initiated", codes: [OrderBusinessStatusEnum.RTO_REQUESTED.code, OrderBusinessStatusEnum.RTO.code] },
  { label: "RTO in transit", codes: [OrderBusinessStatusEnum.RTO_INTRANSIT.code, OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code] },
  { label: "RTO out for delivery", codes: [OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code] },
  { label: "Returned to you", codes: [OrderBusinessStatusEnum.RTO_DELIVERED.code] },
];
const RTO_CODES = [
  OrderBusinessStatusEnum.RTO_REQUESTED.code, OrderBusinessStatusEnum.RTO.code,
  OrderBusinessStatusEnum.RTO_OUT_FOR_DELIVERY.code, OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.RTO_FAILED.code, OrderBusinessStatusEnum.RTO_INTRANSIT.code,
  OrderBusinessStatusEnum.RTO_CONTACT_CUSTOMER_CARE.code, OrderBusinessStatusEnum.RTO_SHIPMENT_DELAY.code,
];
const CANCEL_FAMILY = [
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code,
];

/* Latest history timestamp for any of the given codes. */
const latestTsFor = (statuses, codes) => {
  const codeSet = new Set(codes.map(String));
  let best = null, bestMs = -Infinity;
  (statuses || []).forEach((st) => {
    if (!codeSet.has(String(st.order_status_code || ""))) return;
    const ms = Math.max(safeMs(st.order_status_time), safeMs(st.created_date_time));
    if (ms > bestMs) { bestMs = ms; best = st.order_status_time || st.created_date_time; }
  });
  return best;
};

/* Carrier artifacts exist only once the shipment enters the carrier network; EPOD only after a delivery. */
const PRE_NETWORK_CODES = [
  ...PICKUP_PENDING_CODES,
  OrderBusinessStatusEnum.MANIFESTED.code,
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_AWAITED.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_FAILED.code,
  OrderBusinessStatusEnum.ORDER_CONFIRMATION_RECEIVED_FOR_SHIPMENT_PROCESSING.code,
  OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code,
  OrderBusinessStatusEnum.UNKNOWN.code,
];
const DELIVERED_CODES = [
  OrderBusinessStatusEnum.DELIVERED.code,
  OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.EXCHANGE_DELIVERED.code,
];
const inCarrierNetwork = (code) => Boolean(code) && !PRE_NETWORK_CODES.includes(String(code));
const isDeliveredState = (code) => DELIVERED_CODES.includes(String(code ?? ""));

/* Cancellation is meaningless once the shipment is delivered, cancelled, or mid-cancellation. */
const NON_CANCELLABLE_CODES = [
  OrderBusinessStatusEnum.DELIVERED.code,
  OrderBusinessStatusEnum.RTO_DELIVERED.code,
  OrderBusinessStatusEnum.CANCELLED_ORDER.code,
  OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code,
  OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code,
];
const isCancellable = (code) => !NON_CANCELLABLE_CODES.includes(String(code ?? ""));

/* ---- Status tone system: replicated verbatim from AllShipments.jsx so the list and
   detail pages can never disagree. ONE source of truth for edge + chip + dot + journey stage. ---- */
const STATUS_TONE_FAMILIES = {
  delivered: { chip: "bg-emerald-100 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500", edge: "bg-emerald-400" },
  rto: { chip: "bg-orange-100 text-orange-800 ring-orange-200", dot: "bg-orange-500", edge: "bg-orange-400" },
  cancel: { chip: "bg-rose-100 text-rose-700 ring-rose-200", dot: "bg-rose-500", edge: "bg-rose-400" },
  failed: { chip: "bg-red-100 text-red-800 ring-red-200", dot: "bg-red-500", edge: "bg-red-400" },
  ofd: { chip: "bg-indigo-100 text-indigo-800 ring-indigo-200", dot: "bg-indigo-500", edge: "bg-indigo-400" },
  transit: { chip: "bg-yellow-100 text-yellow-800 ring-yellow-300", dot: "bg-yellow-500", edge: "bg-yellow-400" },
  pickup: { chip: "bg-amber-100 text-amber-800 ring-amber-200", dot: "bg-amber-500", edge: "bg-amber-400" },
  exchange: { chip: "bg-purple-100 text-purple-800 ring-purple-200", dot: "bg-purple-500", edge: "bg-purple-400" },
  payment: { chip: "bg-pink-100 text-pink-800 ring-pink-200", dot: "bg-pink-500", edge: "bg-pink-400" },
  confirmation: { chip: "bg-cyan-100 text-cyan-800 ring-cyan-200", dot: "bg-cyan-500", edge: "bg-cyan-400" },
  attention: { chip: "bg-amber-100 text-amber-800 ring-amber-200", dot: "bg-amber-500", edge: "bg-amber-400" },
  neutral: { chip: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-400", edge: "bg-slate-200" },
};

// Every OrderBusinessStatusEnum code, explicitly: [family, journeyStage 1-5 | null, stageLabel]
const STATUS_CODE_TONE = {
  "0":    ["neutral", null],
  "10":   ["payment", null],
  "20":   ["payment", null],
  "23":   ["confirmation", null],
  "24":   ["failed", null],
  "25":   ["confirmation", null],
  "26":   ["cancel", null],
  "30":   ["pickup", 1, "Order placed"],
  "270":  ["pickup", 1, "AWB registered"],
  "310":  ["pickup", 1, "Manifested"],
  "40":   ["pickup", 2, "Pickup pending"],
  "240":  ["pickup", 2, "Out for pickup"],
  "50":   ["failed", null],
  "60":   ["transit", 3, "Picked up"],
  "70":   ["transit", 3, "In transit"],
  "200":  ["transit", 3, "Delayed / misroute"],
  "220":  ["transit", 3, "Shipment held"],
  "330":  ["transit", 3, "At destination hub"],
  "340":  ["transit", 3, "At origin hub"],
  "350":  ["transit", 3, "Left origin hub"],
  "90":   ["failed", null],
  "80":   ["ofd", 4, "Out for delivery"],
  "100":  ["delivered", 5, "Delivered"],
  "110":  ["failed", null],
  "120":  ["cancel", null],
  "320":  ["cancel", null],
  "360":  ["cancel", null],
  "370":  ["cancel", null],
  "130":  ["rto", 3, "RTO requested"],
  "140":  ["rto", 3, "Returning to origin"],
  "230":  ["rto", 3, "RTO in transit"],
  "260":  ["rto", 3, "RTO delayed"],
  "250":  ["rto", 3, "RTO - contact care"],
  "150":  ["rto", 4, "RTO out for delivery"],
  "160":  ["rto", 5, "Returned to origin"],
  "170":  ["failed", null],
  "180":  ["failed", null],
  "190":  ["failed", null],
  "210":  ["attention", null],
  "280":  ["exchange", null],
  "290":  ["exchange", null],
  "300":  ["exchange", null],
  "9999": ["neutral", null],
};

const statusTone = (code) => {
  const entry = STATUS_CODE_TONE[String(code)];
  const key = entry?.[0] || "neutral";
  const family = STATUS_TONE_FAMILIES[key] || STATUS_TONE_FAMILIES.neutral;
  return { ...family, key, stage: entry?.[1] ?? null, stageLabel: entry?.[2] || null };
};

/* Detail-page extensions keyed by the map's family — halo ring + text ink for rail/timeline. */
const FAMILY_ACCENTS = {
  delivered: { ring: "ring-emerald-100", text: "text-emerald-700" },
  rto: { ring: "ring-orange-100", text: "text-orange-700" },
  cancel: { ring: "ring-rose-100", text: "text-rose-700" },
  failed: { ring: "ring-red-100", text: "text-red-700" },
  ofd: { ring: "ring-indigo-100", text: "text-indigo-700" },
  transit: { ring: "ring-yellow-100", text: "text-yellow-700" },
  pickup: { ring: "ring-amber-100", text: "text-amber-700" },
  exchange: { ring: "ring-purple-100", text: "text-purple-700" },
  payment: { ring: "ring-pink-100", text: "text-pink-700" },
  confirmation: { ring: "ring-cyan-100", text: "text-cyan-700" },
  attention: { ring: "ring-amber-100", text: "text-amber-700" },
  neutral: { ring: "ring-slate-100", text: "text-slate-600" },
};
const familyAccent = (key) => FAMILY_ACCENTS[key] || FAMILY_ACCENTS.neutral;

/* Legacy pill API, now driven entirely by the shared tone map (print layout uses this too). */
function statusPillTone(code) {
  return `ring-1 ${statusTone(code).chip}`;
}

/** "26 Aug, 11:34 pm" in IST; year only when it isn't the current year. Same dialect as the table. */
const formatShortIST = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  const sameYear = date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" }) ===
    now.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric" });
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    ...(sameYear ? {} : { year: "numeric" }),
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "DELHIVERY SURFACE" -> "Delhivery Surface" — calmer, ~15% narrower than caps. */
const titleCaseName = (name) =>
  name ? String(name).toLowerCase().replace(/(^|[\s/-])\w/g, (c) => c.toUpperCase()) : name;
const safeMs = (iso) => {
  if (!iso) return -Infinity;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? -Infinity : d.getTime();
  } catch {
    return -Infinity;
  }
};
/* Unknown money is "—", never a fabricated ₹0.00. */
/** Carrier EDD strings vary; parse leniently, else show the raw value. */
const fmtEdd = (edd) => {
  const d = new Date(edd);
  return Number.isNaN(d.getTime()) ? String(edd) : format(d, "dd MMM yyyy");
};

const prettyMoney = (n) =>
  n == null || n === "" || Number.isNaN(Number(n)) ? "—" : `₹${Number(n).toFixed(2)}`;
// Same lenient parsing + IST rendering as formatInIST, just without seconds.

// Terminal states freeze the age clock - a delivered shipment's age is its
// placed->final-event duration (a transit KPI), not time-since-placed forever.
const AGE_FREEZE_CODES = new Set(["100", "120", "160", "180", "190", "360", "370"]);

/** "1d 3h" / "5h 20m" / "42m" between two instants; null when not computable. */
const fmtAge = (fromISO, toISO) => {
  const a = new Date(fromISO).getTime();
  const b = toISO ? new Date(toISO).getTime() : Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  const mins = Math.floor((b - a) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

/** Day-relative EDD phrasing - "by today" / "by tomorrow" / "in N days" / "N days overdue". */
const eddRelPhrase = (edd) => {
  const d = new Date(edd);
  if (Number.isNaN(d.getTime())) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(new Date())) / 86400000);
  if (days === 0) return "by today";
  if (days === 1) return "by tomorrow";
  if (days > 1) return `in ${days} days`;
  return `${-days} day${days === -1 ? "" : "s"} overdue`;
};

/** Compact "5h ago" for the last-update meta row. */
const agoShort = (iso) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};
const fmtDayTime = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date)) return "—";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const gramsToKg = (g) => (g != null ? `${(Number(g) / 1000).toFixed(2)} kg` : "—");

/** Latest created_date_time from History API business codes (for “Last updated” + Order Date tile) */
const getLatestHistoryUpdate = (statuses = []) => {
  if (!Array.isArray(statuses) || statuses.length === 0) return null;
  const latest = statuses.reduce((best, cur) => {
    const bestMs = safeMs(best?.created_date_time);
    const curMs = safeMs(cur?.created_date_time);
    return curMs >= bestMs ? cur : best;
  });
  return latest?.created_date_time || null;
};

/* ================================ Page ================================ */
export default function ShipmentDetails() {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const { orderRef } = useParams();
  const [sp] = useSearchParams();
  const fromPage = Number(sp.get("page") || state?.fromPage || 1);
  // Back to the list the merchant came from. The list keeps its tab / page / sort / filters in its
  // URL, so a real history back restores it; a fresh tab (no in-app history) falls back to the list.
  const goBackToShipments = useCallback(() => {
    if (location.key && location.key !== "default") navigate(-1);
    else navigate("/all-shipments", { state: { page: fromPage } });
  }, [location.key, navigate, fromPage]);

  const { showToast } = useToast();
  const [shipment, setShipment] = useState(state?.shipment || null);
  const [businessStatuses, setBusinessStatuses] = useState([]);
  // History fetch lifecycle: "loading" | "ready" | "error" — the timeline, rail and
  // status capsule all read this so an in-flight fetch never masquerades as "no history".
  const [historyState, setHistoryState] = useState("loading");
  const [eddInfo, setEddInfo] = useState({ date: null, source: null, kind: "DELIVERY", deltaDays: null, server: false });
  const [weightDisputes, setWeightDisputes] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [labelError, setLabelError] = useState("");

  // Claim modal controls
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalType, setClaimModalType] = useState("damage"); // "damage" | "weight_dispute"

  const openDamageClaim = () => {
    setClaimModalType("damage");
    setClaimModalOpen(true);
  };

  const openWeightDispute = () => {
    setClaimModalType("weight_dispute");
    setClaimModalOpen(true);
  };

  // Support ticket modal controls
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportOrderContext, setSupportOrderContext] = useState(null);
  const { getMerchantId } = useAuth();

  const handleOpenSupport = async () => {
    const merchantId = getMerchantId();
    if (!merchantId) {
      showToast("Please log in to create a support ticket", "error");
      return;
    }

    const sh = shipment;
    const orderRef = sh?.order_reference_number;
    if (!orderRef) {
      showToast("Order reference number not found", "error");
      return;
    }

    try {
      // Try to get existing ticket
      const existingTicket = await ticketService.getTicketByOrder(merchantId, orderRef);
      // If ticket exists, navigate to it
      navigate(`/support/tickets/${existingTicket.public_id}`);
    } catch (err) {
      // If 404, ticket doesn't exist - open create modal
      if (err?.response?.status === 404) {
        setSupportOrderContext({
          orderReferenceNumber: orderRef,
          awbNumber: sh?.awb_number,
          customerName: sh?.drop_name,
          customerPhone: sh?.drop_phone,
          customerEmail: sh?.drop_email,
        });
        setSupportModalOpen(true);
      } else {
        showToast(err?.response?.data?.message || "Failed to check for existing ticket", "error");
      }
    }
  };

  const printRootRef = useRef(null);
  // Which orderRef we've already silently re-validated against the server this mount.
  const refreshedForRef = useRef(null);

  /* Latest code + friendly name */
  const latestBusinessCode = useMemo(() => {
    if (!businessStatuses.length) return null;
    const latest = businessStatuses.reduce((best, cur) => {
      const bestMs = Math.max(safeMs(best.order_status_time), safeMs(best.created_date_time));
      const curMs = Math.max(safeMs(cur.order_status_time), safeMs(cur.created_date_time));
      return curMs >= bestMs ? cur : best;
    });
    return latest?.order_status_code ?? null;
  }, [businessStatuses]);

  const currentStatusName = useMemo(() => {
    if (latestBusinessCode) return getOrderStatusName(String(latestBusinessCode));
    if (shipment?.latest_order_status_code) return getOrderStatusName(String(shipment.latest_order_status_code));
    return shipment?.latest_order_status?.replace(/_/g, " ") || "—";
  }, [latestBusinessCode, shipment]);

  /* Fetch by orderRef — paint instantly from the router-state snapshot when we have one,
     but always re-validate against the server once (silently) so a stale list snapshot
     can never masquerade as the current shipment. */
  useEffect(() => {
    (async () => {
      if (!orderRef) return;
      if (shipment && refreshedForRef.current === orderRef) return;
      const showSpinner = !shipment;
      refreshedForRef.current = orderRef;
      try {
        if (showSpinner) setIsFetching(true);
        const merchantId = localStorage.getItem("merchantId");
        const authToken = localStorage.getItem("authToken");
        if (!merchantId || !authToken) return;
        const res = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`,
          { merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 },
          { headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" } }
        );
        // Background pass keeps the snapshot when the server has nothing newer to say.
        const fresh = res.data?.orders?.[0] || null;
        setShipment((prev) => fresh || prev);
      } catch (e) {
        console.error("Failed to fetch shipment:", e);
      } finally {
        if (showSpinner) setIsFetching(false);
      }
    })();
  }, [orderRef, shipment]);

  /* Fetch history */
  const fetchOrderHistory = useCallback(async (orderReferenceNumber, signal) => {
    setHistoryState("loading");
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || signal?.aborted) return;

      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/order/tracking/history`;
      const response = await axios.post(
        apiUrl,
        { order_reference_number: orderReferenceNumber },
        { headers: { "x-merchant-id": merchantId, Authorization: authToken, "Content-Type": "application/json" }, signal }
      );

      const statuses = (response.data.order_processing_business_status_codes || [])
        .slice()
        .sort((a, b) => safeMs(a.order_status_time || a.created_date_time) - safeMs(b.order_status_time || b.created_date_time));
      setBusinessStatuses(statuses);
      setWeightDisputes(normalizeWeightDisputes(response.data.weight_disputes));
      setEddInfo({
        date: response.data.expected_delivery_date || null,
        source: response.data.edd_source || null,
        kind: response.data.edd_kind || "DELIVERY",
        deltaDays: response.data.delivery_delta_days ?? null,
        server: Boolean(response.data.expected_delivery_date || response.data.delivery_delta_days != null),
      });
      setHistoryState("ready");
    } catch (error) {
      // An aborted request (unmount / re-run) is not an error state.
      if (axios.isCancel(error) || signal?.aborted) return;
      console.error("Error fetching order history:", error);
      setHistoryState("error");
    }
  }, []);

  useEffect(() => {
    const ref = shipment?.order_reference_number || orderRef;
    if (!ref) return;
    const controller = new AbortController();
    fetchOrderHistory(ref, controller.signal);
    return () => controller.abort();
  }, [shipment?.order_reference_number, orderRef, fetchOrderHistory]);

  // A viewed shipment is a successful outcome: it feeds the omnisearch RECENT
  // group and the "Try your AWB ..." hint (one merchant-namespaced store).
  useEffect(() => {
    if (!shipment?.order_reference_number) return;
    pushRecentShipment({
      ref: shipment.order_reference_number,
      awb: shipment.awb_number,
      code: shipment.latest_order_status_code,
    });
  }, [shipment?.order_reference_number, shipment?.awb_number, shipment?.latest_order_status_code]);

  // EDD ladder (server-computed): carrier date first, TAT estimate fallback.
  // Pre-deploy compatibility: fall back to scanning entries for a raw carrier EDD.
  const latestEdd = useMemo(() => {
    if (eddInfo.server) return eddInfo;
    for (let i = businessStatuses.length - 1; i >= 0; i -= 1) {
      const edd = businessStatuses[i]?.carrier_partner_edd;
      if (edd) return { date: edd, source: "CARRIER", kind: "DELIVERY", deltaDays: null, server: false };
    }
    return null;
  }, [eddInfo, businessStatuses]);

  /* Actions */
  const handleCancel = async () => {
    if (!shipment?.awb_number) return;
    const confirmCancel = window.confirm(`Are you sure you want to cancel AWB ${shipment.awb_number}?`);
    if (!confirmCancel) return;
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;
      await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CANCEL_ORDER_URL}`,
        { waybill: shipment.awb_number },
        { headers: { Authorization: authToken, "Content-Type": "application/json" } }
      );
      showToast(`Cancellation requested for AWB ${shipment.awb_number}`, "success");
      goBackToShipments();
    } catch (err) {
      console.error("Cancel failed:", err);
      showToast(err?.response?.data?.message || "Cancellation failed — try again or raise a support ticket", "error");
    }
  };

  const refetchLabel = async () => {
    try {
      setLabelError("");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      if (!merchantId || !authToken || !shipment?.order_reference_number) return;
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_GENERATE_REFRESH_LABEL}`;
      const payload = { merchant_id: merchantId, order_reference_number: shipment.order_reference_number };
      const response = await axios.put(apiUrl, payload, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      const labelUrl = response.data.shipment_label_presigned_url;
      if (labelUrl) {
        const a = document.createElement("a");
        a.href = labelUrl;
        a.download = `${shipment.order_reference_number}-shipment-label.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setLabelError("Label URL not available. Please try again.");
      }
    } catch (error) {
      console.error("Error refetching label:", error);
      setLabelError("Label regeneration failed. Please try again.");
    }
  };

  /* ---------------------- Export PDF (stacked timeline) ---------------------- */
  const handleExportPDF = useCallback(async () => {
    if (!printRootRef.current) return;
    const html2pdf = (await import("html2pdf.js")).default;

    // Show the hidden print layout just for capture
    const node = printRootRef.current;
    const old = node.style.display;
    node.style.display = "block";

    const filename = `${shipment?.order_reference_number || orderRef || "shipment"}-details.pdf`;

    try {
      await html2pdf()
        .set({
          filename,
          margin: [8, 8, 10, 8], // mm
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" },
        })
        .from(node)
        .save();
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      node.style.display = old || "none";
    }
  }, [orderRef, shipment]);

  /* ---------------------- Carrier image download ---------------------- */
  const [carrierImageLoading, setCarrierImageLoading] = useState(null);

  const handleDownloadCarrierImage = async (slug, friendlyLabel) => {
    const awb = shipment?.awb_number;
    if (!awb) {
      showToast("AWB number not available for this shipment", "error");
      return;
    }
    if (carrierImageLoading) return;
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      showToast("Please log in to download carrier images", "error");
      return;
    }
    try {
      setCarrierImageLoading(slug);
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/carrier-images/${slug}?awbNumber=${encodeURIComponent(awb)}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: authToken,
          "x-merchant-id": merchantId,
          "Content-Type": "application/json",
        },
      });
      const data = response?.data || {};
      const presigned =
        data.presignedUrlForImageAccess || data.presigned_url_for_image_access;
      const suggestedName =
        data.suggestedDownloadFileName || data.suggested_download_file_name;
      if (!presigned) {
        showToast(`${friendlyLabel} not yet received from carrier`, "error");
        return;
      }
      const ext =
        (data.fileExtension || data.file_extension || "jpg")
          .toString()
          .replace(/^\./, "");
      const fallbackName =
        safeFilenameSegment(suggestedName) || `${awb}-${slug}.${ext}`;
      await downloadRemoteFile(presigned, fallbackName);
      showToast(`${friendlyLabel} downloaded`, "success");
    } catch (err) {
      if (err && err.name === "DownloadBlockedError") {
        showToast(
          `${friendlyLabel} could not be downloaded directly. Please contact support.`,
          "error",
        );
        return;
      }
      const status = err?.response?.status;
      const code =
        err?.response?.data?.code || err?.response?.data?.errorCode;
      const message =
        err?.response?.data?.message || err?.response?.data?.errorMessage;
      if (status === 404 || code === "CARRIER_IMAGE_NOT_FOUND") {
        showToast(`${friendlyLabel} not yet received for this AWB`, "error");
      } else {
        showToast(message || `Failed to download ${friendlyLabel}`, "error");
      }
    } finally {
      setCarrierImageLoading(null);
    }
  };

  /* Render gates */
  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
        <p className="mt-4 font-medium text-slate-600">Loading shipment details…</p>
      </div>
    );
  }
  if (!shipment) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
        <p className="text-sm text-slate-700">
          No shipment found for reference:{" "}
          <span className="font-mono font-semibold text-slate-900">{orderRef}</span>
        </p>
        <button
          onClick={goBackToShipments}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
        >
          ← Back to Shipments
        </button>
      </div>
    );
  }

  const sh = shipment;
  const packages = Array.isArray(sh.shipment_packages) ? sh.shipment_packages : [];
  const flatItems = packages.flatMap((pkg) => (pkg.items || []).map((item) => ({ ...item, _pkg: pkg })));
  const itemsTotal = flatItems.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);

  const latestHistoryISO = getLatestHistoryUpdate(businessStatuses);
  const latestHistoryPretty = latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A";

  // An OPEN dispute is an action item with a deadline — it gets hoisted next to the money.
  const hasOpenWeightDispute = weightDisputes.some((d) => d?.status === "OPEN");

  const addressLine = (addr, city, dist, state, pin) => [addr, city, dist, state, pin].filter(Boolean).join(", ");

  return (
    <>
      {/* LIVE PAGE (unchanged UI) */}
      <div className="container max-w-7xl mx-auto px-4 lg:px-6 py-8 space-y-8">
        {/* Identity Bar */}
        <Reveal order={0}>
        <IdentityBar
          sh={sh}
          latestEdd={latestEdd}
          latestBusinessCode={latestBusinessCode}
          currentStatusName={currentStatusName}
          onRefetchLabel={refetchLabel}
          onCancel={handleCancel}
          onExportPDF={handleExportPDF}
          labelError={labelError}
          openDamageClaim={openDamageClaim}
          openWeightDispute={openWeightDispute}
          claimModalOpen={claimModalOpen}
          setClaimModalOpen={setClaimModalOpen}
          shipment={shipment}
          claimModalType={claimModalType}
          onSupportClick={handleOpenSupport}
          onBack={goBackToShipments}
          onBookReturn={() => sh?.awb_number && navigate(`/create-reverse-order?awb=${encodeURIComponent(sh.awb_number)}`)}
          onDownloadCarrierImage={handleDownloadCarrierImage}
          carrierImageLoading={carrierImageLoading}
          businessStatuses={businessStatuses}
          historyState={historyState}
        />
        </Reveal>

        {/* Money + weight + activity */}
        <Reveal order={1}>
        <section className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ShipmentChargesCard orderReferenceNumber={sh.order_reference_number || orderRef} />
          <KpiWeightsCompact
            deadGrams={sh.dead_weight_in_grams}
            volGrams={sh.volumetric_weight_in_grams}
            chargedCategory={sh.charged_weight_category}
            dims={sh.shipment_package_dimensions}
            disputes={weightDisputes}
          />
          <MetricTile
            icon={<CalendarDays className="h-4 w-4" />}
            tint="bg-amber-50 text-amber-600"
            label="Shipment Age"
            hintPill={AGE_FREEZE_CODES.has(String(latestBusinessCode ?? "")) ? "Final" : "Running"}
            value={fmtAge(sh.order_placed_date_time,
              AGE_FREEZE_CODES.has(String(latestBusinessCode ?? "")) ? latestHistoryISO : null) || "—"}
            meta={
              latestHistoryISO ? (
                <div className="flex items-baseline justify-between gap-2">
                  <span>Last update</span>
                  <span className="font-medium tabular-nums text-slate-700">{agoShort(latestHistoryISO)}</span>
                </div>
              ) : (
                "Placed — waiting for the carrier's first scan."
              )
            }
          >
            <ShipmentTimeLedger
              placedISO={sh.order_placed_date_time}
              statuses={businessStatuses}
              frozen={AGE_FREEZE_CODES.has(String(latestBusinessCode ?? ""))}
            />
          </MetricTile>
        </section>
        </Reveal>

        {/* Weight dispute — hoisted beside the charges while a dispute is OPEN (action proximity) */}
        {hasOpenWeightDispute ? (
          <Reveal order={2}>
          <OrderWeightDisputeCard disputes={weightDisputes} />
          </Reveal>
        ) : null}

        {/* Metadata */}
        <Reveal order={2}>
        <MetadataGrid
          title="Shipment Metadata"
          items={[
            { k: "Order Type", v: sh.order_type },
            { k: "Pickup Org", v: sh.pickup_organisation },
            { k: "Shipment Type", v: sh.shipment_order_type },
            { k: "Channel", v: sh.channel_name || sh.marketplace_channel_name || "Direct" },
            {
              k: "Declared Value",
              // null stays null so the grid's own "—" fallback renders (no copy button for a dash)
              v: sh.payment_details?.total_amount != null ? prettyMoney(sh.payment_details.total_amount) : null,
              mono: true,
            },
            sh.marketplace_channel_order_number
              ? { k: "Channel Order #", v: sh.marketplace_channel_order_number, mono: true }
              : null,
            { k: "Invoice #", v: sh.invoice_number, mono: true },
          ]}
        />

        </Reveal>

        {/* Addresses */}
        <Reveal order={3}>
        <ResponsiveAddresses
          pickup={{
            name: sh.pickup_name,
            phone: sh.pickup_poc_contact_number,
            email: sh.pickup_poc_email,
            city: sh.pickup_city,
            pin: sh.pickup_pincode,
            addr: addressLine(sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode),
          }}
          delivery={{
            name: sh.drop_name,
            phone: sh.drop_poc_contact_number,
            email: sh.drop_email,
            city: sh.drop_city,
            pin: sh.drop_pincode,
            addr: addressLine(sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode),
          }}
          ret={{
            name: sh.return_name || sh.pickup_name,
            phone: sh.return_phone || sh.pickup_poc_contact_number,
            email: sh.return_email || sh.pickup_poc_email,
            pin: sh.return_pincode || sh.pickup_pincode,
            addr: addressLine(
              sh.return_address || sh.pickup_address,
              sh.return_city || sh.pickup_city,
              sh.return_district || sh.pickup_district,
              sh.return_state || sh.pickup_state,
              sh.return_pincode || sh.pickup_pincode
            ),
          }}
        />

        </Reveal>

        {/* Items */}
        <Reveal order={4}>
        <ItemsSection packages={packages} flatItems={flatItems} labelUrl={sh.order_label_url} sh={sh} itemsTotal={itemsTotal} />

        </Reveal>

        {!hasOpenWeightDispute ? (
          <Reveal order={5}>
          <OrderWeightDisputeCard disputes={weightDisputes} />
          </Reveal>
        ) : null}

        <Reveal order={6}>
        <TrackingTimeline
          businessStatuses={businessStatuses}
          state={historyState}
          onRetry={() => {
            const ref = shipment?.order_reference_number || orderRef;
            if (ref) fetchOrderHistory(ref);
          }}
        />
        </Reveal>

        {/* Footer (uses latest history) */}
        <Reveal order={7}>
        <FooterRail
          lastUpdatedISO={latestHistoryISO}
          onBack={goBackToShipments}
          onRefetch={refetchLabel}
          onCancel={handleCancel}
          latestStatusCode={String(latestBusinessCode ?? sh?.latest_order_status_code ?? "")}
        />
        </Reveal>
      </div>

      {/* PRINT LAYOUT (hidden) */}
      <div
        ref={printRootRef}
        style={{ display: "none" }}
        className="print-root px-4 py-6"
      >
        <div className="max-w-[900px] mx-auto space-y-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 pdf-avoid-break">
            <div>
              <div className="text-xs text-gray-400">Logistics ▸ Shipments ▸ Details</div>
              <h1 className="text-2xl font-semibold text-slate-900">Shipment Details</h1>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-2">
                  <div className="lg:col-span-2">
                    <IdField label="Order Reference" value={sh.order_reference_number} mono />
                  </div>
                  <div className="lg:col-span-2">
                    <IdField label="AWB Number" value={sh.awb_number} mono />
                  </div>
                  <div className="lg:col-span-1">
                    <IdField label="Courier Partner" value={sh.allocated_carrier_partner_public_name} />
                  </div>
                  <div className="lg:col-span-1">
                    {/* Print is paper: no support chat button in a PDF. */}
                    <StatusField
                      label="Latest Status"
                      code={latestBusinessCode ?? sh.latest_order_status_code}
                      value={currentStatusName}
                      shipment={sh}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-right">
              <div>Generated on: {fmtDayTime(new Date().toISOString())}</div>
              <div>Last updated: {latestHistoryISO ? formatInIST(latestHistoryISO) : "N/A"}</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4 pdf-avoid-break">
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Invoice Value</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.payment_details?.total_amount)}</div>
              <div className="text-xs text-slate-600 mt-1">{sh.payment_details?.payment_mode || "-"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                <li>• Items • {flatItems.length}</li>
                <li>• Packages • {packages.length}</li>
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Shipping Cost</div>
              <div className="text-xl font-semibold">{prettyMoney(sh.total_shipping_charge)}</div>
              <div className="text-xs text-slate-600 mt-1">Total Charges</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {sh.total_shipping_tax ? <li>• Tax • {prettyMoney(sh.total_shipping_tax)}</li> : null}
                {sh.awb_number ? <li>• AWB • {sh.awb_number}</li> : null}
              </ul>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Weight</div>
              <div className="text-xl font-semibold">
                {(() => {
                  const deadKg = Number.isFinite(Number(sh.dead_weight_in_grams)) ? Number(sh.dead_weight_in_grams) / 1000 : null;
                  const volKg = Number.isFinite(Number(sh.volumetric_weight_in_grams)) ? Number(sh.volumetric_weight_in_grams) / 1000 : null;
                  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
                  return chargedKg == null ? "N/A" : `${chargedKg.toFixed(2)} kg`;
                })()}
              </div>
              <div className="text-xs text-slate-600 mt-1">Dead • {gramsToKg(sh.dead_weight_in_grams)} &nbsp;&nbsp; Vol • {gramsToKg(sh.volumetric_weight_in_grams)}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Date</div>
              <div className="text-base font-semibold">{sh.order_placed_date_time ? formatInIST(sh.order_placed_date_time) : "N/A"}</div>
              <ul className="text-[11px] text-slate-600 mt-1">
                {latestHistoryISO ? <li>• Updated • {latestHistoryPretty}</li> : null}
                {sh.invoice_number ? <li>• Invoice # • {sh.invoice_number}</li> : null}
              </ul>
            </div>
          </div>

          {/* Metadata */}
          <div className="space-y-3 pdf-avoid-break">
            <h2 className="text-lg font-semibold text-slate-900">Shipment Metadata</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Order Type</div>
                <div className="text-sm font-medium">{sh.order_type || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Pickup Org</div>
                <div className="text-sm font-medium">{sh.pickup_organisation || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Service Type</div>
                <div className="text-sm font-medium">{sh.shipment_order_type || "-"}</div>
              </div>
              <div className="rounded-xl border p-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Charged Weight Category</div>
                <div className="text-sm font-medium">{(sh.charged_weight_category || "").replace(/_/g, " ") || "-"}</div>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Pickup", rows: [
                  ["Name", sh.pickup_name],
                  ["Phone", sh.pickup_poc_contact_number],
                  ["Email", sh.pickup_poc_email],
                  ["Address", [sh.pickup_address, sh.pickup_city, sh.pickup_district, sh.pickup_state, sh.pickup_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Delivery", rows: [
                  ["Customer", sh.drop_name],
                  ["Phone", sh.drop_poc_contact_number],
                  ["Address", [sh.drop_address, sh.drop_city, sh.drop_district, sh.drop_state, sh.drop_pincode].filter(Boolean).join(", ")],
                ]
              },
              {
                title: "Return", rows: [
                  ["Name", sh.return_name || sh.pickup_name],
                  ["Phone", sh.return_phone || sh.pickup_poc_contact_number],
                  ["Address", [
                    sh.return_address || sh.pickup_address,
                    sh.return_city || sh.pickup_city,
                    sh.return_district || sh.pickup_district,
                    sh.return_state || sh.pickup_state,
                    sh.return_pincode || sh.pickup_pincode,
                  ].filter(Boolean).join(", ")],
                ]
              },
            ].map((card, idx) => (
              <div key={idx} className="rounded-xl border p-4">
                <h3 className="text-sm font-semibold text-slate-800">{card.title}</h3>
                <div className="mt-2 space-y-1.5 text-sm">
                  {card.rows.map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k}: </span><span className="text-slate-800">{v || "N/A"}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Items</h2>
                <div className="text-sm text-slate-500">{flatItems.length} {flatItems.length === 1 ? "item" : "items"} across {packages.length} {packages.length === 1 ? "package" : "packages"}</div>
              </div>
            </div>

            {packages.map((pkg, idx) => {
              const items = Array.isArray(pkg.items) ? pkg.items : [];
              const subtotal = items.reduce((sum, it) => sum + Number(it.quantity ?? 0) * Number(it.item_payment_details?.unit_price ?? 0), 0);
              return (
                <div key={idx} className="rounded-xl border overflow-hidden pdf-avoid-break">
                  <div className="px-4 py-2 text-sm bg-slate-50 flex items-center justify-between">
                    <div className="font-medium">Package #{idx + 1}</div>
                    <div className="text-slate-600">
                      {/* Per-package weight fields don't exist on this API — same shipment-level source as the live page. */}
                      Dead: {gramsToKg(sh.dead_weight_in_grams)} • Vol: {gramsToKg(sh.volumetric_weight_in_grams)}
                      {pkg.dimensions_in_cm ? ` • ${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm` : ""}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-600 bg-white">
                      <tr>
                        <th className="px-4 py-2 text-left">SKU</th>
                        <th className="px-4 py-2 text-left">Item Name</th>
                        <th className="px-4 py-2 text-left">Qty</th>
                        <th className="px-4 py-2 text-left">Unit Price</th>
                        <th className="px-4 py-2 text-left">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-3 text-center text-slate-500">No items in this package.</td></tr>
                      ) : items.map((it, j) => {
                        const sku = it.item_general_details?.sku || "-";
                        const name = it.item_general_details?.item_name || "-";
                        const qty = Number(it.quantity ?? 0);
                        const unit = Number(it.item_payment_details?.unit_price ?? 0);
                        const total = qty * unit;
                        return (
                          <tr key={j} className="border-t">
                            <td className="px-4 py-2 font-mono text-xs">{sku}</td>
                            <td className="px-4 py-2">{name}</td>
                            <td className="px-4 py-2">{qty}</td>
                            <td className="px-4 py-2">{prettyMoney(unit)}</td>
                            <td className="px-4 py-2 font-semibold">{prettyMoney(total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 flex justify-end">
                    <div className="min-w-[240px] rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Subtotal</span>
                        <span className="font-medium">{prettyMoney(subtotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {flatItems.length > 0 && (
              <div className="flex justify-end">
                <div className="min-w-[280px] rounded-xl border px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">{prettyMoney(itemsTotal)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-slate-600">Shipping</span>
                    <span className="font-medium">{prettyMoney(sh.total_shipping_charge)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t flex items-center justify-between">
                    <span className="text-slate-800 font-semibold">Order Total</span>
                    <span className="text-slate-900 font-semibold">
                      {prettyMoney(itemsTotal + Number(sh.total_shipping_charge ?? 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* STACKED TIMELINE (latest on top) */}
          <div className="pdf-avoid-break">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Shipment Tracking History</h2>
            <div className="text-sm text-slate-600 mb-4">
              Order Ref: <span className="font-medium text-slate-800">{sh.order_reference_number}</span>
              {sh.awb_number ? <> &nbsp;•&nbsp; AWB: <span className="font-medium text-slate-800">{sh.awb_number}</span></> : null}
            </div>

            <div className="space-y-3">
              {[...businessStatuses].sort((a, b) => {
                // latest first by created_date_time (fallback order_status_time)
                const aMs = Math.max(safeMs(a.created_date_time), safeMs(a.order_status_time));
                const bMs = Math.max(safeMs(b.created_date_time), safeMs(b.order_status_time));
                return bMs - aMs;
              }).map((st, i) => {
                const whenIso = st.created_date_time || st.order_status_time;
                const when = whenIso ? formatInIST(whenIso) : "N/A";
                const name = getOrderStatusName(String(st.order_status_code));
                return (
                  <div key={i} className="rounded-xl border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{name}</div>
                      <div className="text-xs text-slate-600 whitespace-nowrap">
                        {when}
                        {whenIso && agoShort(whenIso) ? <span className="text-slate-400"> ({agoShort(whenIso)})</span> : null}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      {st.order_location ? <div>Location: {st.order_location}</div> : null}
                      {st.order_remarks ? <div className="mt-0.5">{st.order_remarks}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Support Ticket Modal */}
      <CreateTicketModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
        }}
        onSuccess={(ticket) => {
          setSupportModalOpen(false);
          setSupportOrderContext(null);
          navigate(`/support/tickets/${ticket.public_id}`);
        }}
        orderContext={supportOrderContext}
      />

      {/* Print styles */}
      <style>{`
        .pdf-avoid-break { page-break-inside: avoid; }
        .print-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>
    </>
  );
}

/* ============================== Components ============================== */


function IdField({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <span title={value || ''} className={`${mono ? 'font-mono' : 'font-medium'} text-slate-900 break-all`}>
          {value || '—'}
        </span>
      </div>
    </div>
  );
}

function StatusField({ label, code, value, shipment, onSupportClick }) {
  const tone = statusPillTone(code);
  const statusCodeStr = String(code ?? "");
  const needsSupport = STATUSES_REQUIRING_SUPPORT.includes(statusCodeStr);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${tone}`}>{value || "—"}</div>
        {needsSupport && shipment && onSupportClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSupportClick();
            }}
            className="inline-flex items-center justify-center p-1.5 bg-[#3877FC] hover:bg-[#2A59BD] text-white rounded-lg transition-colors"
            title="Create Support Ticket"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* -------- Identity bar -------- */
function IdentityBar({
  sh,
  latestEdd,
  latestBusinessCode,
  currentStatusName,
  onRefetchLabel,
  onCancel,
  labelError,
  onExportPDF,
  openDamageClaim,
  openWeightDispute,
  claimModalOpen,
  setClaimModalOpen,
  shipment,
  claimModalType,
  onSupportClick,
  onBack,
  onBookReturn,
  onDownloadCarrierImage,
  carrierImageLoading,
  businessStatuses = [],
  historyState = "ready",
}) {
  const reduce = useReducedMotion();
  const statusCode = String(latestBusinessCode ?? sh.latest_order_status_code ?? "");
  const isPickupPending = PICKUP_PENDING_CODES.includes(statusCode);

  // The page states its state: header edge, chip, dot and journey bar all read from ONE map.
  const tone = statusTone(latestBusinessCode ?? sh.latest_order_status_code);

  const paymentMode = String(sh.payment_details?.payment_mode || sh.order_type || "").toUpperCase();
  const isCod = paymentMode.includes("COD");
  const deadKg = Number.isFinite(Number(sh.dead_weight_in_grams)) ? Number(sh.dead_weight_in_grams) / 1000 : null;
  const volKg = Number.isFinite(Number(sh.volumetric_weight_in_grams)) ? Number(sh.volumetric_weight_in_grams) / 1000 : null;
  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
  const trackingUrl = sh.awb_number ? `https://tracking.wayuvega.in/track?awb=${encodeURIComponent(sh.awb_number)}` : null;
  const shipmentKind = String(sh.shipment_order_type || "").toUpperCase();
  const isReverseKind = shipmentKind === "RVP" || shipmentKind === "REVERSE";
  const statusAsOfISO = getLatestHistoryUpdate(businessStatuses) || sh.latest_status_timestamp || null;
  // A return starts here: only a delivered forward shipment with an AWB (same rule as the return desk).
  const canBookReturn = Boolean(onBookReturn && sh.awb_number)
    && assessReturnEligibility({ shipment_order_type: sh.shipment_order_type, latest_order_status_code: statusCode }).ok;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* status edge — one glance = one state */}
      <div className={`h-1 ${tone.edge}`} />

      {/* identity */}
      <div className="px-5 pb-5 pt-4 md:px-6">
        {/* kicker: the table's micro-tag dialect */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            {onBack ? (
              // icon-only, same chrome as the Support button opposite - reads as page
              // navigation, not as another kicker chip in the row
              <button
                onClick={onBack}
                title="Back to shipments"
                aria-label="Back to shipments"
                className="mr-1.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            ) : null}
            {shipmentKind ? (
              <span className={`inline-flex flex-shrink-0 items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ${isReverseKind ? "bg-purple-50 ring-purple-200 text-purple-700" : "bg-sky-50 ring-sky-200 text-sky-700"}`}>
                {shipmentKind}
              </span>
            ) : null}
            {(sh.channel_name || sh.marketplace_channel_name) ? (
              <span className="inline-block min-w-0 truncate rounded bg-indigo-50 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-indigo-700 ring-1 ring-indigo-200" title={sh.channel_name || sh.marketplace_channel_name}>
                {sh.channel_name || sh.marketplace_channel_name}
              </span>
            ) : null}
            {paymentMode ? (
              <span className={`inline-flex flex-shrink-0 items-center rounded px-1 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ${isCod ? "bg-amber-50 ring-amber-200 text-amber-700" : "bg-emerald-50 ring-emerald-200 text-emerald-700"}`}>
                {paymentMode}
              </span>
            ) : null}
          </div>
          {onSupportClick ? (
            <button
              onClick={onSupportClick}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-600 transition-colors hover:text-slate-900"
              title="Create Support Ticket"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Support
            </button>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          {/* identifiers speak the gutter-label language of the table's order cell */}
          <div className="flex min-w-0 flex-col gap-0.5">
            {sh.awb_number ? (
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="w-8 flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider text-violet-500">Ref</span>
                <span className="min-w-0 break-all font-mono text-[13px] font-semibold leading-snug text-slate-900" title={sh.order_reference_number || undefined}>
                  {sh.order_reference_number || "—"}
                </span>
                {sh.order_reference_number ? <HeaderCopy value={sh.order_reference_number} label="order reference" /> : null}
              </span>
            ) : (
              /* No AWB yet: the Ref is the shipment's real identity — it takes the hero slot,
                 and the AWB line demotes to a quiet fact instead of shouting "Not assigned". */
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="w-8 flex-shrink-0 pb-1 text-[9px] font-semibold uppercase tracking-wider text-violet-500">Ref</span>
                <h1 className="min-w-0 break-all font-mono text-[26px] font-semibold leading-tight tracking-tight text-slate-900 md:text-[30px]" title={sh.order_reference_number || undefined}>
                  {sh.order_reference_number || "—"}
                </h1>
                {sh.order_reference_number ? <HeaderCopy value={sh.order_reference_number} label="order reference" /> : null}
              </span>
            )}

            {sh.awb_number ? (
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="w-8 flex-shrink-0 pb-1 text-[9px] font-semibold uppercase tracking-wider text-[#3877FC]">Awb</span>
                <h1 className="min-w-0">
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all font-mono text-[26px] font-semibold leading-tight tracking-tight text-[#2A59BD] transition-colors hover:text-[#1C3B7E] md:text-[30px]"
                    title="Click to open live tracking"
                  >
                    {sh.awb_number}
                  </a>
                </h1>
                <HeaderCopy value={sh.awb_number} label="AWB number" />
              </span>
            ) : (
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="w-8 flex-shrink-0 pb-1 text-[9px] font-semibold uppercase tracking-wider text-[#3877FC]">Awb</span>
                <span className="text-xs text-slate-400">Not assigned yet</span>
              </span>
            )}

          </div>

          {/* status capsule — the table's status cell, verbatim visual */}
          <div className="flex flex-col gap-0.5 sm:items-end">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className={`inline-flex w-fit min-w-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold leading-snug ring-1 ${tone.chip}`} title={currentStatusName}>
                {currentStatusName}
              </span>
              {/* Contextual support entry, only for the statuses that actually warrant a ticket. */}
              {onSupportClick && STATUSES_REQUIRING_SUPPORT.includes(statusCode) ? (
                <button
                  onClick={onSupportClick}
                  className="inline-flex flex-shrink-0 items-center justify-center rounded-lg bg-[#3877FC] p-1 text-white transition-colors hover:bg-[#2A59BD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                  title="Create Support Ticket"
                  aria-label="Create support ticket"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </span>
            {statusAsOfISO ? (
              <span className="text-[11px] text-slate-500">
                {formatShortIST(statusAsOfISO)}
                {agoShort(statusAsOfISO) ? <span className="text-slate-400"> ({agoShort(statusAsOfISO)})</span> : null}
              </span>
            ) : historyState === "loading" ? (
              /* history still in flight — hold the slot with a quiet bar instead of a null-jump */
              <span className={`h-3 w-24 rounded bg-slate-100 ${reduce ? "" : "animate-pulse"}`} aria-hidden />
            ) : (
              <span className="text-[11px] text-slate-500">—</span>
            )}
          </div>
        </div>

        {/* fact strip — every hero fact in one labeled, divided row */}
        <div className="mt-4 flex flex-wrap items-stretch gap-y-3 divide-x divide-slate-200/80">
          {sh.awb_number ? (
            <div className="flex flex-col gap-0.5 px-5 first:pl-0">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Track</span>
              <span className="flex items-center gap-1">
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-[13px] font-medium text-[#2A59BD] underline-offset-2 transition-colors hover:text-[#1C3B7E] hover:underline"
                  title="Open live tracking"
                >
                  Live tracking
                  <ExternalLink className="h-3 w-3" />
                </a>
                <HeaderCopy value={trackingUrl} label="tracking link" />
              </span>
            </div>
          ) : null}
          <div className="flex flex-col gap-0.5 px-5 first:pl-0">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Placed</span>
            <span className="text-[13px] font-medium text-slate-800">
              {formatShortIST(sh.order_placed_date_time)}
              {agoShort(sh.order_placed_date_time) ? <span className="text-slate-400"> ({agoShort(sh.order_placed_date_time)})</span> : null}
            </span>
          </div>
          {sh.allocated_carrier_partner_public_name ? (
            <div className="flex flex-col gap-0.5 px-5">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Courier</span>
              <span className="text-[13px] font-medium text-slate-800">{titleCaseName(sh.allocated_carrier_partner_public_name)}</span>
            </div>
          ) : null}
          {chargedKg != null ? (
            <div className="flex flex-col gap-0.5 px-5">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Billed weight</span>
              <span className="text-[13px] font-medium tabular-nums text-slate-800">{chargedKg.toFixed(2)} kg</span>
            </div>
          ) : null}
          {latestEdd && (latestEdd.server || (tone.stage != null && tone.stage >= 3 && tone.stage < 5)) ? (() => {
            const { date, source, kind, deltaDays } = latestEdd;
            if (deltaDays != null) {
              const early = deltaDays < 0;
              const late = deltaDays > 0;
              const n = Math.abs(deltaDays);
              const verdict = late ? `${n} day${n === 1 ? "" : "s"} late` : early ? `${n} day${n === 1 ? "" : "s"} early` : "On time";
              return (
                <div className="flex flex-col gap-0.5 px-5">
                  <span className={`text-[9px] font-semibold uppercase tracking-wider ${kind === "RETURN" ? "text-orange-600" : "text-emerald-600"}`}>
                    {kind === "RETURN" ? "Returned" : "Delivered"}
                  </span>
                  <span
                    className={`text-[13px] font-semibold tabular-nums ${late ? "text-rose-600" : "text-emerald-600"}`}
                    title={`vs ${source === "CARRIER" ? "courier EDD" : "estimated EDD"} ${fmtEdd(date)}`}
                  >
                    {verdict}
                  </span>
                </div>
              );
            }
            const isReturn = kind === "RETURN";
            const isEst = source === "ESTIMATED";
            return (
              <div className="flex flex-col gap-0.5 px-5">
                <span className={`text-[9px] font-semibold uppercase tracking-wider ${isReturn ? "text-orange-600" : isEst ? "text-amber-600" : "text-[#3877FC]"}`}>
                  {isReturn ? (isEst ? "Est. return" : "Return EDD") : isEst ? "Est. delivery" : "EDD"}
                </span>
                <span
                  className={`text-[13px] tabular-nums ${isEst ? "font-medium text-slate-800" : isReturn ? "font-semibold text-orange-700" : "font-semibold text-[#2A59BD]"}`}
                  title={isReturn
                    ? (isEst ? "Estimated return to origin (RTO start + courier TAT)" : "Return date from the courier")
                    : (isEst ? "Estimated from pickup date + courier TAT" : "Expected delivery date from the courier")}
                >
                  {fmtEdd(date)}
                  {(() => {
                    const rel = eddRelPhrase(date);
                    if (!rel) return null;
                    return <span className={`font-medium ${rel.endsWith("overdue") ? "text-rose-500" : "text-slate-400"}`}> ({rel})</span>;
                  })()}
                </span>
              </div>
            );
          })() : null}
        </div>
      </div>

      {/* toolbar — intent groups */}
      <div className="flex flex-wrap items-start gap-x-8 gap-y-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 md:px-6">
        <div>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Documents</div>
          <div className="flex flex-wrap gap-2">
            <ActionBtn
              as="a"
              href={sh.order_label_url || undefined}
              disabled={!sh.order_label_url}
              title={!sh.order_label_url ? "Label not available yet" : "Open the shipping label"}
              icon={<Tag className="h-4 w-4" />}
            >
              View Label
            </ActionBtn>
            {isPickupPending ? (
              <ActionBtn onClick={onRefetchLabel} icon={<RefreshCw className="h-4 w-4" />}>
                Refetch Label
              </ActionBtn>
            ) : null}
            {inCarrierNetwork(statusCode) ? (
              <>
                <ActionBtn
                  onClick={() => onDownloadCarrierImage?.("qc", "QC image")}
                  disabled={!sh.awb_number || carrierImageLoading === "qc"}
                  title={!sh.awb_number ? "AWB number is required" : "Download QC image"}
                  icon={<Camera className="h-4 w-4" />}
                >
                  {carrierImageLoading === "qc" ? "Downloading…" : "QC Image"}
                </ActionBtn>
                <ActionBtn
                  onClick={() => onDownloadCarrierImage?.("sorter", "Sorter image")}
                  disabled={!sh.awb_number || carrierImageLoading === "sorter"}
                  title={!sh.awb_number ? "AWB number is required" : "Download sorter image"}
                  icon={<Layers className="h-4 w-4" />}
                >
                  {carrierImageLoading === "sorter" ? "Downloading…" : "Sorter Image"}
                </ActionBtn>
              </>
            ) : null}
            {isDeliveredState(statusCode) ? (
              <ActionBtn
                onClick={() => onDownloadCarrierImage?.("epod", "Proof of delivery")}
                disabled={!sh.awb_number || carrierImageLoading === "epod"}
                title={!sh.awb_number ? "AWB number is required" : "Download proof of delivery"}
                icon={<FileCheck className="h-4 w-4" />}
              >
                {carrierImageLoading === "epod" ? "Downloading…" : "EPOD"}
              </ActionBtn>
            ) : null}
            {onExportPDF ? (
              <ActionBtn onClick={onExportPDF} title="Download this page as a PDF" icon={<Download className="h-4 w-4" />}>
                Export PDF
              </ActionBtn>
            ) : null}
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Claims</div>
          <div className="flex flex-wrap gap-2">
            <ActionBtn onClick={openDamageClaim} variant="warnOutline" icon={<ShieldAlert className="h-4 w-4" />}>
              Raise Damage Claim
            </ActionBtn>
            <ActionBtn onClick={openWeightDispute} variant="successOutline" icon={<Video className="h-4 w-4" />}>
              Upload Packaging Video
            </ActionBtn>
          </div>
        </div>

        {canBookReturn ? (
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#2A59BD]">Return</div>
            <div className="flex flex-wrap gap-2">
              <ActionBtn onClick={onBookReturn} variant="accentOutline" title="Open the return desk with this shipment filled in" icon={<RotateCcw className="h-4 w-4" />}>
                Book a Return
              </ActionBtn>
            </div>
          </div>
        ) : null}

        {isCancellable(statusCode) ? (
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-rose-500">Order</div>
            <div className="flex flex-wrap gap-2">
              <ActionBtn onClick={onCancel} variant="danger" icon={<XCircle className="h-4 w-4" />}>
                Cancel Shipment
              </ActionBtn>
            </div>
          </div>
        ) : null}
      </div>

      {/* journey rail */}
      <MilestoneRail businessStatuses={businessStatuses} state={historyState} isReverse={isReverseKind} />

      <ClaimUploadModal
        open={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        shipment={shipment}
        claimType={claimModalType}
      />
      {labelError && <p className="px-5 pb-3 text-xs text-rose-600 md:px-6">{labelError}</p>}
    </section>
  );
}

/* The journey at a glance — forward or RTO story, with a terminal band when the story has ended. */
function MilestoneRail({ businessStatuses = [], state = "ready", isReverse = false }) {
  const reduce = useReducedMotion();

  // History failed to load: no rail beats a rail that lies at "Placed".
  if (state === "error") return null;
  // History in flight: hold the slot with a quiet bar so the rail doesn't pop in later.
  if (state === "loading" && !businessStatuses.length) {
    return (
      <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-4 md:px-6">
        <div className={`h-1.5 w-full max-w-sm rounded-full bg-slate-200 ${reduce ? "" : "animate-pulse"}`} aria-hidden />
      </div>
    );
  }

  const seen = new Set(businessStatuses.map((st) => String(st.order_status_code || "")));
  const isRto = RTO_CODES.some((c) => seen.has(c));
  const cancelledCode = CANCEL_FAMILY.find((c) => seen.has(c));
  const stages = isRto ? RTO_STAGES : JOURNEY_STAGES;

  const doneFlags = stages.map((stage) => stage.codes.some((c) => seen.has(c)));
  let lastDone = -1;
  doneFlags.forEach((d, i) => { if (d) lastDone = i; });

  // Terminal band facts (cancelled / lost / damaged / delivered / returned)
  const lostTs = seen.has(OrderBusinessStatusEnum.LOST.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.LOST.code]) : null;
  const damagedTs = seen.has(OrderBusinessStatusEnum.DAMAGED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.DAMAGED.code]) : null;
  const deliveredTs = !isRto && seen.has(OrderBusinessStatusEnum.DELIVERED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.DELIVERED.code]) : null;
  const returnedTs = isRto && seen.has(OrderBusinessStatusEnum.RTO_DELIVERED.code) ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.RTO_DELIVERED.code]) : null;
  const cancelPendingTs = seen.has(OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code) && !cancelledCode
    ? latestTsFor(businessStatuses, [OrderBusinessStatusEnum.ORDER_CANCELLATION_PENDING.code]) : null;

  // Any terminal/exception band below means the journey is over — a "live" ping would be a lie.
  const terminalBand = Boolean(cancelPendingTs || lostTs || damagedTs || deliveredTs || returnedTs);

  const CANCEL_COPY = {
    [OrderBusinessStatusEnum.CANCELLED_ORDER.code]: "Cancelled",
    [OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_CARRIER.code]: "Pickup cancelled by courier",
    [OrderBusinessStatusEnum.PICKUP_CANCELLED_BY_SHIPPER.code]: "Pickup cancelled",
    [OrderBusinessStatusEnum.ORDER_CANCELLED_BY_END_CUSTOMER_DURING_CONFIRMATION.code]: "Cancelled during confirmation",
  };

  // Band inks come from the tone map's families: cancel→rose, failed→red, rto→orange, delivered→emerald.
  const BAND_TONES = {
    rose: "border-rose-100 bg-rose-50/70 text-rose-700",
    red: "border-red-100 bg-red-50/70 text-red-700",
    amber: "border-amber-100 bg-amber-50/70 text-amber-800",
    orange: "border-orange-100 bg-orange-50/70 text-orange-800",
    emerald: "border-emerald-100 bg-emerald-50/70 text-emerald-700",
  };
  const Band = ({ tone, icon, children }) => (
    <div className={`flex items-center gap-2 border-t px-5 py-3 text-sm font-medium md:px-6 ${BAND_TONES[tone] || BAND_TONES.emerald}`}>
      {icon}
      <span>{children}</span>
    </div>
  );

  // Cancelled: the journey never really happened — band alone tells the whole story.
  if (cancelledCode) {
    const ts = latestTsFor(businessStatuses, [cancelledCode]);
    return (
      <Band tone="rose" icon={<XCircle className="h-4 w-4 flex-shrink-0" />}>
        <span className="font-semibold">{CANCEL_COPY[cancelledCode] || "Cancelled"}</span>
        {ts ? <span className="font-normal"> on {fmtDayTime(ts)}</span> : null}
      </Band>
    );
  }

  /* The rail retells the journey in the tone map's own colours: each leg wears its family —
     placed/pickup amber → transit yellow → OFD indigo → delivered emerald; RTO legs run orange. */
  const rail = lastDone === -1 ? null : (
    <div className="border-t border-slate-100 bg-slate-50/50 px-5 pb-4 pt-3 md:px-6">
      <div className="flex items-start overflow-x-auto pt-3 pb-1 -mx-1 px-1">
        {stages.map((stage, i) => {
          const done = i < lastDone;
          const current = i === lastDone;
          const reached = done || current;
          const finalStage = current && i === stages.length - 1;
          const legTone = statusTone(stage.codes[0]);
          const legAccent = familyAccent(legTone.key);
          const reachedTs = reached ? latestTsFor(businessStatuses, stage.codes) : null;
          // Reverse pickups end at the merchant, not the customer: "Delivered" reads "Received back".
          const stageLabel = isReverse && !isRto && i === stages.length - 1 ? "Received back" : stage.label;
          return (
            <React.Fragment key={stage.label}>
              {i > 0 ? (
                <div className="relative mt-[13px] h-0.5 min-w-4 flex-1 overflow-hidden rounded-full bg-slate-200">
                  {i <= lastDone ? (
                    reduce ? (
                      <span className={`absolute inset-0 ${legTone.dot}`} />
                    ) : (
                      <motion.span
                        className={`absolute inset-0 origin-left ${legTone.dot}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.35, delay: 0.2 + i * 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                      />
                    )
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-col items-center gap-1.5 px-1">
                <span
                  aria-current={current ? "step" : undefined}
                  className={`relative flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                    reached
                      ? `${legTone.dot} text-white shadow-sm ${current ? `ring-4 ${legAccent.ring}` : ""}`
                      : "border-2 border-slate-200 bg-white text-slate-300"
                  }`}
                >
                  <span className="sr-only">{stageLabel}</span>
                  {reached ? <CheckIcon className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                  {current && !finalStage && !reduce && !terminalBand ? (
                    <span className={`absolute inset-0 animate-ping rounded-full opacity-30 ${legTone.dot}`} />
                  ) : null}
                </span>
                <span className={`whitespace-nowrap text-[11px] ${reached ? `font-semibold ${current ? legAccent.text : "text-slate-700"}` : "font-medium text-slate-400"}`}>
                  {stageLabel}
                </span>
                {reachedTs ? (
                  <span className="-mt-0.5 whitespace-nowrap text-[10px] leading-none tabular-nums text-slate-400">
                    {formatShortIST(reachedTs)}
                  </span>
                ) : null}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {rail}
      {cancelPendingTs ? (
        <Band tone="amber" icon={<Clock className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-semibold">Cancellation requested</span>
          <span className="font-normal"> on {fmtDayTime(cancelPendingTs)}</span>
        </Band>
      ) : null}
      {lostTs ? (
        <Band tone="red" icon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-semibold">Shipment lost</span>
          <span className="font-normal"> — reported on {fmtDayTime(lostTs)}. Raise a claim from the Claims actions above.</span>
        </Band>
      ) : null}
      {damagedTs ? (
        <Band tone="red" icon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-semibold">Shipment damaged</span>
          <span className="font-normal"> — reported on {fmtDayTime(damagedTs)}. Raise a damage claim from the Claims actions above.</span>
        </Band>
      ) : null}
      {deliveredTs ? (
        <Band tone="emerald" icon={<CheckIcon className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-semibold">{isReverse ? "Received back" : "Delivered"}</span>
          <span className="font-normal"> on {fmtDayTime(deliveredTs)}</span>
        </Band>
      ) : null}
      {returnedTs ? (
        <Band tone="orange" icon={<RotateCcw className="h-4 w-4 flex-shrink-0" />}>
          <span className="font-semibold">Returned to you</span>
          <span className="font-normal"> on {fmtDayTime(returnedTs)}</span>
        </Band>
      ) : null}
    </>
  );
}

/* Page-load choreography: each section rises in, in reading order. Honors reduced motion. */
function Reveal({ order = 0, children }) {
  const reduce = useReducedMotion();
  if (reduce) return children;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, delay: order * 0.07, ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* Tiny copy affordance for header identifiers — flips to a check for a beat. */
function HeaderCopy({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      onClick={onCopy}
      className="rounded p-0.5 text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
    </button>
  );
}

/* One button system for the identity bar: a single accent, neutral secondaries, outlined intents. */
function ActionBtn({ as, href, onClick, disabled, title, icon, variant = "neutral", children }) {
  const base =
    "inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30";
  const variants = {
    neutral: disabled
      ? "border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
      : "border border-slate-200 bg-white text-slate-600 hover:text-slate-900",
    danger: disabled
      ? "border border-rose-200 bg-rose-50 text-rose-300 cursor-not-allowed"
      : "border border-rose-200 bg-white text-rose-600 hover:text-rose-800",
    warnOutline: "border border-amber-200 bg-white text-amber-700 hover:text-amber-900",
    successOutline: "border border-emerald-200 bg-white text-emerald-700 hover:text-emerald-900",
    accentOutline: "border border-[#3877FC]/35 bg-white text-[#2A59BD] hover:bg-[#3877FC]/[0.06] hover:text-[#1E46A0]",
  };
  const cls = `${base} ${variants[variant] ?? variants.neutral}`;
  if (as === "a" && !disabled) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} title={title}>
        {icon}
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={cls} title={title}>
      {icon}
      {children}
    </button>
  );
}

/* -------- Metric tile (supports inlineValue) -------- */
function MetricTile({ label, value, icon, tint = "bg-slate-100 text-slate-500", hintPill, meta, compactValue = false, children }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      {/* label row — one line, nothing competes with it */}
      <div className="flex items-center gap-2">
        {icon ? <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${tint}`}>{icon}</span> : null}
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {hintPill ? (
          <span className="ml-auto whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {hintPill}
          </span>
        ) : null}
      </div>

      <div className={`${compactValue ? "text-lg" : "text-xl"} mt-2.5 font-semibold leading-tight tracking-tight tabular-nums text-slate-900`}>
        {value}
      </div>

      {/* body — the tile's own ledger, in the same grammar as the charges lines; content-sized, never stretched */}
      {children ? <div className="mt-3 border-t border-slate-100 pt-3">{children}</div> : null}

      {meta ? (
        <div className="mt-auto pt-3">
          <div className="border-t border-slate-100 pt-2.5 text-xs leading-relaxed text-slate-500">{meta}</div>
        </div>
      ) : null}
    </div>
  );
}

/* -------- Ledger row shared by the small tiles: label + value, a quiet meta line under the label -------- */
function TileLedgerRow({ label, value, meta, emphasis = false, valueTone = "text-slate-900" }) {
  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className={`min-w-0 break-words text-sm ${emphasis ? "font-semibold text-slate-900" : "font-medium text-slate-800"}`}>{label}</span>
        {value != null ? <span className={`flex-shrink-0 whitespace-nowrap text-sm tabular-nums ${emphasis ? "font-semibold" : "font-medium"} ${valueTone}`}>{value}</span> : null}
      </div>
      {meta ? <div className="mt-0.5 text-xs text-slate-500">{meta}</div> : null}
    </li>
  );
}

const fmtShortDayTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

/* -------- Weights tile: the whole story — dead, volumetric (with dims), carrier's scale, and which one billed -------- */
function KpiWeightsCompact({ deadGrams, volGrams, chargedCategory, dims, disputes = [] }) {
  const deadKg = Number.isFinite(Number(deadGrams)) ? Number(deadGrams) / 1000 : null;
  const volKg = Number.isFinite(Number(volGrams)) ? Number(volGrams) / 1000 : null;
  const chargedKg = deadKg == null && volKg == null ? null : Math.max(deadKg || 0, volKg || 0);
  // No weights at all → no billing basis to assert: hide the pill and the billed-dot.
  const hasCharged = chargedKg != null;

  // Which weight was billed: trust the server category; fall back to the larger value.
  const cat = String(chargedCategory || "").toUpperCase();
  const basis = cat.includes("VOL") ? "vol" : cat.includes("DEAD") ? "dead" : (volKg || 0) > (deadKg || 0) ? "vol" : "dead";

  // Shipment-level dimensions from the search-orders response (shipment_package_dimensions).
  const dimsLabel =
    dims && dims.length != null && dims.breadth != null && dims.height != null
      ? `${dims.length} × ${dims.breadth} × ${dims.height} ${dims.unit || "cm"}`
      : null;

  const kg = (v) => (v == null ? "—" : `${v.toFixed(2)} kg`);

  // The carrier's own scale, when it disagreed with the booking (the latest dispute on this AWB).
  const dispute = (disputes || []).find((d) => d && d.status !== "VOID") || null;
  const carrierKg = dispute && Number.isFinite(Number(dispute.carrierWeightGrams)) ? Number(dispute.carrierWeightGrams) / 1000 : null;
  const varianceKg = dispute && Number.isFinite(Number(dispute.varianceGrams)) ? Number(dispute.varianceGrams) / 1000 : null;
  const disputeNote = dispute
    ? dispute.status === "OPEN"
      ? "Weight dispute open — review it below"
      : dispute.status === "MERCHANT_ACCEPTED" || dispute.status === "DISPUTE_REJECTED_BY_WAYUVEGA"
        ? "Carrier's weight accepted"
        : dispute.status === "DISPUTE_ACCEPTED_BY_WAYUVEGA"
          ? "Resolved in your favour"
          : "Weight dispute raised"
    : null;

  return (
    <MetricTile
      label="Charged Weight"
      icon={<ScaleIcon className="h-4 w-4" />}
      tint="bg-violet-50 text-violet-600"
      hintPill={hasCharged ? (basis === "vol" ? "Volumetric" : "Dead") : null}
      value={hasCharged ? kg(chargedKg) : "—"}
      meta={hasCharged ? "Billed on the higher of dead and volumetric weight. Amounts include GST." : "Weights appear once the shipment is booked."}
    >
      <ul className="divide-y divide-slate-100">
        <TileLedgerRow label="Dead weight" value={kg(deadKg)} meta="On the scale, as booked" emphasis={hasCharged && basis === "dead"} />
        <TileLedgerRow label="Volumetric weight" value={kg(volKg)} meta={dimsLabel ? `From ${dimsLabel}` : "No dimensions recorded"} emphasis={hasCharged && basis === "vol"} />
        {carrierKg != null ? (
          <TileLedgerRow
            label="Carrier measured"
            value={kg(carrierKg)}
            valueTone={varianceKg != null && varianceKg > 0 ? "text-amber-700" : "text-slate-900"}
            meta={`${varianceKg != null ? `${varianceKg > 0 ? "+" : ""}${varianceKg.toFixed(2)} kg vs booked · ` : ""}${disputeNote}`}
          />
        ) : null}
      </ul>
    </MetricTile>
  );
}

/* -------- Time tile: where the days went — one row per milestone, with the gap since the previous one -------- */
const TIME_MILESTONES = [
  ["50", "Pickup failed"], ["60", "Picked up"], ["70", "In transit"], ["80", "Out for delivery"],
  ["110", "Delivery attempt failed"], ["100", "Delivered"], ["320", "Cancellation requested"], ["120", "Cancelled"],
  ["130", "Return started"], ["140", "Return started"], ["160", "Returned to you"], ["180", "Lost"], ["190", "Damaged"],
];
const TIME_MILESTONE_LABEL = Object.fromEntries(TIME_MILESTONES);

function ShipmentTimeLedger({ placedISO, statuses, frozen }) {
  // First time each milestone was reached, in time order; "Return started" counts once whichever code came first.
  const seen = new Set();
  const rows = [];
  if (placedISO && safeMs(placedISO) > 0) rows.push({ key: "placed", label: "Placed", at: placedISO });
  (statuses || []).forEach((st) => {
    const code = String(st?.order_status_code ?? "");
    const label = TIME_MILESTONE_LABEL[code];
    const at = st?.order_status_time || st?.created_date_time;
    if (!label || !at || safeMs(at) <= 0) return;
    const dedupeKey = label === "Return started" ? "return" : code;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    rows.push({ key: dedupeKey, label, at });
  });
  rows.sort((a, b) => safeMs(a.at) - safeMs(b.at));

  if (!rows.length) {
    return <p className="text-xs text-slate-500">The timeline starts when the carrier picks up the shipment.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((row, i) => {
        const gap = i > 0 ? fmtAge(rows[i - 1].at, row.at) : null;
        const last = i === rows.length - 1;
        return (
          <TileLedgerRow
            key={row.key}
            label={row.label}
            value={gap ? `+${gap}` : null}
            valueTone="text-slate-600"
            meta={fmtShortDayTime(row.at)}
            emphasis={last && frozen}
          />
        );
      })}
    </ul>
  );
}

/* -------- Metadata: one quiet sheet — small-caps labels, weighted values, mono refs -------- */
function MetadataGrid({ title, items }) {
  const clean = (items || []).filter(Boolean);
  if (clean.length === 0) return null;
  // Tailwind needs literal class names — no template interpolation.
  const COLS = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };
  const lgColCount = Math.min(clean.length, 6);
  const lgCols = COLS[lgColCount];
  // Hairlines via gap-px over a slate wash — divide-* can't survive both the sm and lg wrap
  // points. Breakpoint-scoped filler cells keep a partially-filled last row white, not slate.
  const lgFillers = (lgColCount - (clean.length % lgColCount)) % lgColCount;
  const smFillers = clean.length % 2;
  return (
    <section>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <SectionHeader title={title} icon={<ClipboardList className="h-5 w-5" />} tint="bg-[#3877FC]/10 text-[#2A59BD]" />
        <dl className={`grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2 ${lgCols}`}>
          {clean.map(({ k, v, mono }, i) => (
            <div key={i} className="bg-white px-5 py-4 md:px-6">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{k}</dt>
              {mono ? (
                <dd className="mt-1.5 flex min-w-0 items-start gap-1">
                  <span className="break-all font-mono text-[13px] font-medium leading-snug text-slate-800">{v || "—"}</span>
                  {v ? <HeaderCopy value={v} label={k} /> : null}
                </dd>
              ) : (
                <dd className="mt-1.5 text-sm font-medium capitalize text-slate-800">
                  {String(v || "—").toLowerCase()}
                </dd>
              )}
            </div>
          ))}
          {smFillers ? <div className="hidden bg-white sm:block lg:hidden" aria-hidden /> : null}
          {Array.from({ length: lgFillers }).map((_, i) => (
            <div key={`filler-${i}`} className="hidden bg-white lg:block" aria-hidden />
          ))}
        </dl>
      </div>
    </section>
  );
}

/* -------- Addresses: a route sheet headed by the table's amber→emerald rail —
   origin and destination sit under their own endpoint dots; return collapses when identical -------- */
function ResponsiveAddresses({ pickup, delivery, ret }) {
  const norm = (x) => String(x || "").trim().toLowerCase();
  const returnSameAsPickup =
    norm(ret?.addr) === norm(pickup?.addr) && norm(ret?.phone) === norm(pickup?.phone);


  const ContactBlock = ({ role, roleClass, dot, person }) => (
    <div className="min-w-0 flex-1 px-5 py-5">
      <div className="flex items-center gap-1.5">
        {dot}
        <span className={`text-[9px] font-semibold uppercase tracking-wider ${roleClass}`}>{role}</span>
      </div>

      <div className="mt-2.5 text-sm font-semibold text-slate-900">{person?.name || "—"}</div>

      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-slate-600">
        {person?.phone ? (
          <span className="inline-flex items-center gap-1">
            <Phone className="h-3 w-3 text-slate-400" />
            <a href={`tel:${person.phone}`} className="transition-colors hover:text-[#2A59BD]">{person.phone}</a>
            <HeaderCopy value={person.phone} label="phone" />
          </span>
        ) : null}
        {person?.email ? (
          <span className="inline-flex min-w-0 items-center gap-1">
            <Mail className="h-3 w-3 flex-shrink-0 text-slate-400" />
            <a href={`mailto:${person.email}`} className="truncate transition-colors hover:text-[#2A59BD]" title={person.email}>
              {person.email}
            </a>
          </span>
        ) : null}
        {person?.pin ? (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3 text-slate-400" />
            <span className="tabular-nums">{person.pin}</span>
            <HeaderCopy value={person.pin} label="pincode" />
          </span>
        ) : null}
      </div>

      <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{person?.addr || "—"}</p>
    </div>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader title="Route" icon={<MapIcon className="h-5 w-5" />} tint="bg-[#3877FC]/10 text-[#2A59BD]" />
      <div className="flex flex-col divide-y divide-slate-100 lg:flex-row lg:divide-x lg:divide-y-0">
        <ContactBlock
          role="From"
          roleClass="text-amber-600"
          dot={<span className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-100" aria-hidden />}
          person={pickup}
        />
        <ContactBlock
          role="To"
          roleClass="text-emerald-600"
          dot={<span className="h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100" aria-hidden />}
          person={delivery}
        />
        {!returnSameAsPickup ? (
          <ContactBlock
            role="Return"
            roleClass="text-slate-500"
            dot={<RotateCcw className="h-3 w-3 text-amber-500" aria-hidden />}
            person={ret}
          />
        ) : null}
      </div>

      {returnSameAsPickup ? (
        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-2.5 text-xs text-slate-500">
          <RotateCcw className="h-3.5 w-3.5 text-amber-500" />
          Return address is the same as pickup
        </div>
      ) : null}
    </section>
  );
}

/* -------- Section header / empty state -------- */
function SectionHeader({ title, subtitle, right, icon, tint = "bg-slate-100 text-slate-600" }) {
  // In-card header: every section heading lives INSIDE its card, never floats above it.
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5 md:px-6">
      <div className="flex items-center gap-2.5 min-w-0">
        {icon ? (
          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${tint}`}>
            {icon}
          </div>
        ) : null}
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}
function EmptyState({ label }) {
  return <div className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-500 bg-slate-50">{label}</div>;
}

/* -------- Packages & items: goods only — money lives in the Charges card above -------- */
function ItemsSection({ packages, flatItems, labelUrl, sh, itemsTotal }) {
  // Shipment-level weight/dims describe the (single) package; per-package fields don't exist on this API.
  const dims = sh?.shipment_package_dimensions;
  const dimsLabel =
    dims && dims.length != null ? `${dims.length}×${dims.breadth}×${dims.height} ${dims.unit || "cm"}` : null;
  const deadLabel =
    sh?.dead_weight_in_grams != null ? gramsToKg(sh.dead_weight_in_grams) : null;
  const singleBandMeta = [deadLabel, dimsLabel].filter(Boolean).join("  ·  ");

  return (
    <section>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader
        title="Packages & items"
        icon={<PackageIcon className="h-5 w-5" />}
        tint="bg-[#3877FC]/10 text-[#2A59BD]"
        subtitle={`${flatItems.length} ${flatItems.length === 1 ? "item" : "items"} · ${packages.length} ${packages.length === 1 ? "package" : "packages"}`}
        right={
          labelUrl ? (
            <a
              href={labelUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition-colors hover:text-slate-900"
            >
              <Tag className="h-3.5 w-3.5" />
              View Label
            </a>
          ) : null
        }
      />

      {packages.length === 0 ? (
        <div className="px-5 py-6"><EmptyState label="No package/items found for this shipment." /></div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {packages.map((pkg, pkgIdx) => {
                  const items = Array.isArray(pkg.items) ? pkg.items : [];
                  const bandMeta =
                    packages.length === 1
                      ? singleBandMeta
                      : pkg.dimensions_in_cm
                        ? `${pkg.dimensions_in_cm.length}×${pkg.dimensions_in_cm.breadth}×${pkg.dimensions_in_cm.height} cm`
                        : "";
                  return (
                    <React.Fragment key={pkgIdx}>
                      {/* package band */}
                      <tr>
                        <td colSpan={4} className="border-b border-slate-100 bg-slate-50/60 px-5 py-2.5 md:px-6">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm font-semibold text-slate-800">Package {pkgIdx + 1}</span>
                            {bandMeta ? (
                              <span className="font-mono text-xs text-slate-400">{bandMeta}</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {/* column heads per package block */}
                      <tr className="border-b border-slate-100 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        <th className="px-5 py-2 font-semibold md:px-6">Item</th>
                        <th className="w-20 px-4 py-2 text-right font-semibold">Qty</th>
                        <th className="w-32 px-4 py-2 text-right font-semibold">Unit Price</th>
                        <th className="w-36 px-5 py-2 text-right font-semibold md:px-6">Total</th>
                      </tr>
                      {items.map((it, j) => {
                        const sku = it.item_general_details?.sku;
                        const name = it.item_general_details?.item_name || "—";
                        const qty = Number(it.quantity ?? 0);
                        const unit = Number(it.item_payment_details?.unit_price ?? 0);
                        return (
                          <tr key={`${pkgIdx}-${j}`} className="border-b border-slate-100 text-sm transition-colors last:border-b-0 hover:bg-slate-50/60">
                            <td className="px-5 py-3 md:px-6">
                              <div className="break-words font-medium text-slate-900 line-clamp-2" title={name}>{name}</div>
                              {sku ? (
                                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                                  <span className="break-all font-mono">SKU {sku}</span>
                                  <HeaderCopy value={sku} label="SKU" />
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{qty}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{prettyMoney(unit)}</td>
                            <td className="px-5 py-3 text-right font-semibold tabular-nums text-slate-900 md:px-6">{prettyMoney(qty * unit)}</td>
                          </tr>
                        );
                      })}
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-5 py-4 text-center text-sm text-slate-400">No items in this package.</td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {flatItems.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-slate-100">
                    <td colSpan={2} />
                    <td className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Items subtotal</td>
                    <td className="px-5 py-3 text-right text-sm font-semibold tabular-nums text-slate-900 md:px-6">{prettyMoney(itemsTotal)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      )}
      </div>
    </section>
  );
}

/* -------- Tracking history  -------- */
const TIMELINE_FILTERS = [
  ["ALL", "All"],
  ["EXC", "Exceptions"],
  ["LOC", "Location"],
  ["ATT", "Attempts"],
];
const TIMELINE_FILTER_NOUN = { EXC: "exception", LOC: "location", ATT: "attempt" };

function TrackingTimeline({ businessStatuses = [], state = "ready", onRetry }) {
  const [filter, setFilter] = useState("ALL");
  const reduce = useReducedMotion();

  const shell = (body) => (
    <section>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <SectionHeader title="Shipment Tracking History" icon={<Activity className="h-5 w-5" />} tint="bg-[#3877FC]/10 text-[#2A59BD]" />
        {body}
      </div>
    </section>
  );

  // Fetch in flight: hold the card open with placeholder rows instead of claiming emptiness.
  if (state === "loading" && !businessStatuses.length) {
    return shell(
      <div className="space-y-5 px-5 py-5 md:px-6" aria-busy="true" aria-label="Loading tracking history">
        {["w-3/4", "w-2/3", "w-3/5", "w-1/2"].map((w, i) => (
          <div key={i} className={`flex items-start gap-3 ${reduce ? "" : "animate-pulse"}`}>
            <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-slate-200" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className={`h-3 rounded bg-slate-200 ${w}`} />
              <div className="h-2.5 w-1/3 rounded bg-slate-100" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (state === "error") {
    return shell(
      <div className="flex flex-col items-center gap-3 px-5 py-10 text-center md:px-6">
        <AlertTriangle className="h-5 w-5 text-slate-400" />
        <p className="text-sm font-medium text-slate-600">Couldn't load tracking history</p>
        {onRetry ? (
          <button
            onClick={onRetry}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  // "No history" is only a fact once the fetch has actually come back clean.
  if (!businessStatuses.length) {
    return shell(<div className="px-5 py-6 md:px-6"><EmptyState label="No tracking statuses found." /></div>);
  }

  // normalize + sort latest → oldest
  const rows = [...businessStatuses]
    .map(s => ({
      ...s,
      whenMs: Math.max(safeMs(s.created_date_time), safeMs(s.order_status_time)),
      whenISO: s.created_date_time || s.order_status_time,
    }))
    .sort((a, b) => b.whenMs - a.whenMs);

  const passes = (s) => {
    if (filter === "ALL") return true;
    const code = String(s.order_status_code || "");
    if (filter === "EXC") return ["110", "120", "170"].includes(code);          // cancelled/failed/rtx
    if (filter === "LOC") return Boolean(s.order_location);
    if (filter === "ATT") return /attempt/i.test(s.order_remarks || "");
    return true;
  };

  const filtered = rows.filter(passes);

  // group by calendar day — key off the sanitized whenMs: parseISO on a malformed
  // carrier timestamp throws RangeError, safeMs already swallowed it to -Infinity.
  const groups = filtered.reduce((acc, s) => {
    const d = s.whenMs > -Infinity ? format(new Date(s.whenMs), "EEE, dd MMM yyyy") : "Unknown";
    (acc[d] ||= []).push(s);
    return acc;
  }, {});

  const groupEntries = Object.entries(groups);
  // Running row offsets so the entrance stagger reads strictly top-to-bottom even
  // when a single day holds more than ten events.
  let runningIndex = 0;
  const groupOffsets = groupEntries.map(([, list]) => {
    const start = runningIndex;
    runningIndex += list.length;
    return start;
  });

  return (
    <section>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader
        title="Shipment Tracking History"
        icon={<Activity className="h-5 w-5" />}
        tint="bg-[#3877FC]/10 text-[#2A59BD]"
        right={
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            {TIMELINE_FILTERS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
                className={`rounded-md px-2.5 py-1 text-xs transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
                  filter === key
                    ? "bg-white font-semibold text-slate-900 shadow-sm"
                    : "font-medium text-slate-500 hover:text-slate-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <CopyHistoryButton items={filtered} />
        </div>
        }
      />

      {/* log-book v2: gradient rail, floating day pills, milestone markers, staggered entrance */}
      {filtered.length === 0 ? (
        /* Events exist — the current filter just hides them all. Say so, in the card. */
        <div className="px-5 py-6 md:px-6">
          <EmptyState label={`No ${TIMELINE_FILTER_NOUN[filter] || "matching"} events for this shipment.`} />
        </div>
      ) : (
      <div className="max-h-[560px] overflow-y-auto overscroll-contain">
        {groupEntries.map(([day, list], groupIdx) => (
          <div key={day} className="relative">
            {/* day pill floats on the rail — sticks while its day scrolls */}
            <div className="sticky top-0 z-20 flex items-center gap-2 bg-white/95 px-5 pb-1 pt-4 backdrop-blur-sm md:px-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-[#3877FC]" aria-hidden />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{day}</span>
                <span className="text-[11px] text-slate-400">{list.length}</span>
              </span>
            </div>

            <ul className="relative pb-2 pt-1">
              {/* rail fades in and out of each day block */}
              <span
                className="absolute bottom-0 left-[27px] top-0 w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent"
                aria-hidden
              />
              {list.map((st, idx) => {
                const isLatest = groupIdx === 0 && idx === 0;
                const name = getOrderStatusName(String(st.order_status_code));
                const code = String(st.order_status_code || "");
                const isException = ["110", "120", "170"].includes(code);
                const isDelivered = code === "100";
                const when = fmtDayTime(st.whenISO);
                const rowIndex = groupOffsets[groupIdx] + idx;
                // Every event wears its family from the shared tone map — the log-book reads in colour.
                const rowTone = statusTone(code);
                const rowAccent = familyAccent(rowTone.key);

                return (
                  <TimelineRow key={st.whenISO + idx} order={rowIndex}>
                    <li
                      className={`group relative py-3 pl-12 pr-5 transition-colors md:pr-6 ${
                        isLatest ? "bg-gradient-to-r from-[#3877FC]/[0.06] to-transparent" : "hover:bg-slate-50/60"
                      }`}
                    >
                      {/* marker: milestones get an icon coin, routine updates a family-tinted dot */}
                      {isException || isDelivered ? (
                        <span
                          className={`absolute left-[27px] top-[16px] flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full text-white shadow-sm ring-4 ring-white ${rowTone.dot}`}
                        >
                          {isException ? <AlertTriangle className="h-3 w-3" /> : <CheckIcon className="h-3 w-3" />}
                        </span>
                      ) : (
                        <span
                          className={`absolute left-[27px] top-[22px] h-2.5 w-2.5 -translate-x-1/2 rounded-full ring-4 ring-white ${rowTone.dot}`}
                        >
                          {isLatest && !reduce ? (
                            <span className={`absolute inset-0 animate-ping rounded-full opacity-40 ${rowTone.dot}`} />
                          ) : null}
                        </span>
                      )}

                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className={`text-sm ${isLatest ? "font-semibold" : "font-medium"} ${isException ? rowAccent.text : isLatest ? "text-slate-900" : "text-slate-800"}`}>{name}</span>
                        {isLatest ? (
                          <span className="rounded-full bg-[#3877FC]/10 px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-[#2A59BD]">
                            Latest
                          </span>
                        ) : null}
                        {isException ? (
                          <span className={`rounded-full px-2 py-px text-[11px] font-semibold ring-1 ${rowTone.chip}`}>
                            Exception
                          </span>
                        ) : null}
                        <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-slate-400">
                          {when}
                          {agoShort(st.whenISO) ? <span className="text-slate-300"> · {agoShort(st.whenISO)}</span> : null}
                        </span>
                      </div>

                      {(st.order_location || st.order_remarks) ? (
                        <div className="mt-0.5 text-[13px] leading-snug text-slate-500">
                          {st.order_location ? (
                            <span className="font-medium text-slate-600">{st.order_location}</span>
                          ) : null}
                          {st.order_location && st.order_remarks ? <span className="mx-1.5 text-slate-300">·</span> : null}
                          {st.order_remarks ? <span className="italic">“{st.order_remarks}”</span> : null}
                        </div>
                      ) : null}
                    </li>
                  </TimelineRow>
                );
              })}
            </ul>
            {groupIdx < groupEntries.length - 1 ? <div className="mx-5 border-t border-slate-100 md:mx-6" /> : null}
          </div>
        ))}
      </div>
      )}
      </div>
    </section>
  );
}

/* Rows slide in along the rail, top-down. */
function TimelineRow({ order = 0, children }) {
  const reduce = useReducedMotion();
  if (reduce) return children;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: Math.min(order * 0.04, 0.6), ease: [0.22, 0.61, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function CopyHistoryButton({ items }) {
  const [copied, setCopied] = useState(false);
  const empty = !items.length;
  const copy = () => {
    const lines = items.map(s => {
      const t = s.whenISO ? formatInIST(s.whenISO) : "N/A";
      return `${t} | ${getOrderStatusName(String(s.order_status_code))} | ${s.order_location || "-"} | ${s.order_remarks || "-"}`;
    });
    navigator.clipboard?.writeText(lines.join("\n"));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      disabled={empty}
      className={`ml-2 inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
        copied
          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
          : empty
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
            : "border-slate-200 bg-white text-slate-600 hover:text-slate-900"
      }`}
      title={empty ? "Nothing to copy — no events match the current filter" : "Copy the timeline as text — handy for support tickets"}
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy history"}
    </button>
  );
}


/* -------- Footer rail (driven by history latest) -------- */
function FooterRail({ lastUpdatedISO, onBack, onRefetch, onCancel, latestStatusCode }) {
  const isPickupPending = PICKUP_PENDING_CODES.includes(latestStatusCode);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">
        Last updated: <span className="font-semibold text-slate-700">{fmtDayTime(lastUpdatedISO)}</span>
        {agoShort(lastUpdatedISO) ? <span className="text-slate-400"> ({agoShort(lastUpdatedISO)})</span> : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onBack} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">
          ← Back to Shipments
        </button>
        {isPickupPending ? (
          <button
            onClick={onRefetch}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
          >
            Refetch Label
          </button>
        ) : null}
        {isCancellable(latestStatusCode) ? (
          <button onClick={onCancel} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-sm font-medium text-rose-600 transition-colors hover:text-rose-800">
            Cancel Shipment
          </button>
        ) : null}
      </div>
    </div>
  );
}
